import axios, { type AxiosResponse } from 'axios';

import { type CartPreview } from '../../shared/types';
import { cartCache } from '../helper/cartCache';
import { retryWithBackoff } from '../helper/retryWithBackoff';

const EMPTY_CART: CartPreview = {
    cartId: '',
    cartVersion: 0,
    itemsCount: 0,
    itemsProductIds: [],
    vouchersCount: 0,
    rebuilt: false,
    trackingLabel: '',
    vouchers: [],
    vouchersTotal: {
        amount: '0',
        currencyCode: 'EUR',
    },
};

const REQUEST_TIMEOUT_MS = 5_000;

let controller: AbortController;

async function fetchCart(): Promise<CartPreview> {
    const cachedCart = cartCache.get();
    if (cachedCart) {
        return cachedCart;
    }

    if (controller !== undefined) controller.abort();
    controller = new AbortController();

    try {
        const cart = await retryWithBackoff(
            async () => {
                const response: AxiosResponse<CartPreview> = await axios.get('/api/cart', {
                    timeout: REQUEST_TIMEOUT_MS,
                    signal: controller.signal,
                });
                return response.data;
            },
            {
                maxRetries: 2,
                initialDelayMs: 300,
                retryableStatusCodes: [408, 429, 502, 503, 504],
            },
        );

        cartCache.set(cart);
        return cart;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status && error.response.status >= 500) {
            const staleCart = cartCache.getStale();
            if (staleCart) {
                return staleCart;
            }

            return EMPTY_CART;
        }

        throw error;
    }
}

export { fetchCart };
